import Decimal from "decimal.js";

/**
 * A promoção por produto: um desconto em reais que vale num período de datas
 * com uma faixa de horas dentro dele, em certos dias da semana, ou nos dois.
 *
 * A mesma regra roda no servidor, em
 * `pallas-api/common/features/promocao_produto.js`. Não é duplicação por
 * descuido: `criar_pedido` recalcula o subtotal lendo o produto de volta do
 * Firebase e recusa o pedido quando não bate com o que o cliente viu. Se os
 * dois lados divergirem, todo pedido com promoção morre no checkout com
 * "Sacola desatualizada". Mexeu aqui, mexa lá.
 */

/**
 * Decompõe um instante no fuso da loja nas peças que a regra compara em
 * separado, como o `DateTime` do C#: dia da semana, data (sem hora) e hora do
 * dia (sem data).
 *
 * Tudo no fuso da **loja**, não no do aparelho — senão um cliente em Portugal
 * veria a promoção das 18h começar às 22h. `utcOffsetMinutes` vem publicado
 * junto com a loja; sem ele (menu publicado por uma versão anterior) sobra o
 * fuso local, que é o comportamento antigo.
 */
const shopParts = (ms, utcOffsetMinutes) => {
  const shifted = utcOffsetMinutes != null;
  const d = shifted ? new Date(ms + utcOffsetMinutes * 60000) : new Date(ms);

  const year = shifted ? d.getUTCFullYear() : d.getFullYear();
  const month = shifted ? d.getUTCMonth() : d.getMonth();
  const dayOfMonth = shifted ? d.getUTCDate() : d.getDate();
  const hours = shifted ? d.getUTCHours() : d.getHours();
  const minutes = shifted ? d.getUTCMinutes() : d.getMinutes();
  const seconds = shifted ? d.getUTCSeconds() : d.getSeconds();
  const millis = shifted ? d.getUTCMilliseconds() : d.getMilliseconds();

  return {
    weekDay: shifted ? d.getUTCDay() : d.getDay(),
    // chaves numéricas ordenáveis: 20260823 para a data, e os milissegundos
    // decorridos desde a meia-noite para a hora
    date: year * 10000 + month * 100 + dayOfMonth,
    timeOfDay: ((hours * 60 + minutes) * 60 + seconds) * 1000 + millis,
  };
};

/**
 * O instante cai no período? Data e hora entram em comparações **separadas**,
 * como o `promocaoTempo()` do Pallas:
 *
 *     agora.Date >= de.Date && agora.Date <= ate.Date &&
 *     agora.TimeOfDay >= de.TimeOfDay && agora.TimeOfDay <= ate.TimeOfDay
 *
 * Ou seja, o período não é um intervalo contínuo: é uma faixa de dias com uma
 * faixa de horas que se repete todo dia dentro dela — "de 01/03 a 31/03, todo
 * dia das 18h às 22h". Bordas inclusivas dos dois lados, nas duas peças.
 *
 * Consequência que parece bug e não é: uma faixa que vira a meia-noite (fim
 * antes do início, 22h às 2h) nunca satisfaz as duas comparações de hora, e
 * deixa a promoção desligada. É o que o C# faz, e é o lado em que o cardápio
 * tem que errar — divergir da API derruba o pedido no checkout.
 */
const isInPeriod = (current, from, to) =>
  current.date >= from.date &&
  current.date <= to.date &&
  current.timeOfDay >= from.timeOfDay &&
  current.timeOfDay <= to.timeOfDay;

export function isPromotionActive(
  promotion,
  now = new Date(),
  utcOffsetMinutes,
) {
  if (!promotion || !Decimal(promotion.discount || 0).greaterThan(0)) {
    return false;
  }

  const current = shopParts(now.getTime(), utcOffsetMinutes);
  const inPeriod =
    promotion.from != null &&
    promotion.to != null &&
    isInPeriod(
      current,
      shopParts(promotion.from, utcOffsetMinutes),
      shopParts(promotion.to, utcOffsetMinutes),
    );
  const onWeekDay = !!promotion.weekDays[current.weekDay];

  return promotion.allConditions
    ? inPeriod && onWeekDay
    : inPeriod || onWeekDay;
}

/**
 * Os preços do produto agora: os promocionais e os cheios, lado a lado.
 *
 * `minPrice`/`maxPrice` vêm do Firebase já somados às perguntas obrigatórias
 * mais baratas e mais caras, e são calculados na publicação — que é manual.
 * Por isso o desconto não pode estar embutido neles: uma promoção que começa
 * amanhã nunca começaria. Ele é abatido aqui, na leitura.
 *
 * O desconto efetivo é limitado ao preço base porque o piso tem que cair na
 * base, antes dos adicionais. Sem isso, um desconto de R$ 50 num lanche de
 * R$ 8 com adicional obrigatório de R$ 3 anunciaria "por R$ -39,00" em vez de
 * "por R$ 3,00" — que é o que a API vai cobrar.
 */
export function getProductPrices(product, now = new Date(), utcOffsetMinutes) {
  const fullPrices = {
    fullBasicPrice: product.basicPrice,
    fullMinPrice: product.minPrice,
    fullMaxPrice: product.maxPrice,
  };
  const basicPrice = Decimal(product.basicPrice || 0);

  if (
    !basicPrice.greaterThan(0) ||
    !isPromotionActive(product.promotion, now, utcOffsetMinutes)
  ) {
    return {
      ...fullPrices,
      isPromotional: false,
      discount: "0",
      basicPrice: product.basicPrice,
      minPrice: product.minPrice,
      maxPrice: product.maxPrice,
    };
  }

  const discount = Decimal.min(product.promotion.discount, basicPrice);

  return {
    ...fullPrices,
    isPromotional: true,
    discount: discount.toString(),
    basicPrice: basicPrice.minus(discount).toString(),
    minPrice: Decimal(product.minPrice || 0)
      .minus(discount)
      .toString(),
    maxPrice: Decimal(product.maxPrice || 0)
      .minus(discount)
      .toString(),
  };
}

/**
 * A porcentagem de desconto exibida ao lado do preço, inteira: o `-47%`.
 *
 * A base é o `fullMinPrice`, que é o preço que aparece riscado, para a conta
 * bater com os dois números que o cliente vê lado a lado.
 */
export function getDiscountPercent(prices) {
  const full = Decimal(prices.fullMinPrice || 0);
  if (!full.greaterThan(0)) {
    return 0;
  }

  return full
    .minus(prices.minPrice || 0)
    .dividedBy(full)
    .times(100)
    .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
    .toNumber();
}
